
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthProvider';
import { AuthFormValues, mapSupabaseUser } from '@/types/auth';

export function useAuthSubmit() {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { user, setUser } = useAuth();

  const handleSignIn = async (values: AuthFormValues) => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: values.email,
        password: values.password,
      });

      if (error) {
        toast.error('Sign in failed', {
          description: error.message,
        });
        return false;
      }

      if (data.user) {
        setUser?.(mapSupabaseUser(data.user));
        toast.success('Signed in successfully');
        navigate('/');
        return true;
      }
    } catch (error) {
      console.error('Sign in error:', error);
      toast.error('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
    return false;
  };

  const handleSignUp = async (values: AuthFormValues) => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.auth.signUp({
        email: values.email,
        password: values.password,
        options: {
          data: {
            full_name: values.fullName || '',
          },
        },
      });

      if (error) {
        toast.error('Sign up failed', {
          description: error.message,
        });
        return false;
      }

      if (data.user) {
        toast.success('Account created successfully', {
          description: 'Please check your email to verify your account',
        });
        navigate('/verify-email', { state: { email: values.email } });
        return true;
      }
    } catch (error) {
      console.error('Sign up error:', error);
      toast.error('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
    return false;
  };

  const handleSignOut = async () => {
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        toast.error('Sign out failed', {
          description: error.message,
        });
        return false;
      }

      setUser?.(null);
      toast.success('Signed out successfully');
      navigate('/signin');
      return true;
    } catch (error) {
      console.error('Sign out error:', error);
      toast.error('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
    return false;
  };

  const handlePasswordReset = async (email: string) => {
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        toast.error('Password reset failed', {
          description: error.message,
        });
        return false;
      }

      toast.success('Password reset email sent', {
        description: 'Please check your email for the reset link',
      });
      return true;
    } catch (error) {
      console.error('Password reset error:', error);
      toast.error('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
    return false;
  };

  const handleUpdatePassword = async (password: string) => {
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password,
      });

      if (error) {
        toast.error('Password update failed', {
          description: error.message,
        });
        return false;
      }

      toast.success('Password updated successfully');
      navigate('/profile');
      return true;
    } catch (error) {
      console.error('Password update error:', error);
      toast.error('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
    return false;
  };

  // Add these methods for signup compatibility
  const handleAuthSignin = async (email: string, password: string) => {
    return handleSignIn({ email, password });
  };

  const handleAuthSignup = async (userData: AuthFormValues) => {
    const result = await handleSignUp(userData);
    
    // Return an object with user information and exists flag
    return { 
      id: result ? 'new_user_id' : '', 
      exists: false 
    };
  };

  return {
    isLoading,
    handleSignIn,
    handleSignUp,
    handleSignOut,
    handlePasswordReset,
    handleUpdatePassword,
    handleAuthSignin,
    handleAuthSignup,
  };
}
